import { Button } from '@timelyapp/react';
import type { DropdownOption } from 'components/DropdownListWithOptions';
import PopoverDropdownList from 'components/popovers/PopoverDropdownList';
import {
  NON_MONEY_WIDGETS,
  WIDGET_BILLABLE_VS_NON_BILLABLE_DONUT,
  WIDGET_BILLED_VS_UNBILLED_DONUT,
  WIDGET_CLIENTS_DONUT,
  WIDGET_LABELS_DONUT,
  WIDGET_LINKED_WORK_DONUT,
  WIDGET_PROJECTS_DONUT,
  WIDGET_TEAMS_DONUT,
} from 'constants/widgets';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReportData } from 'redux/modules/superreports.actions';
import type { WidgetDraft } from 'redux/modules/superreports.types';
import {
  changeWidgetType,
  updateWidgetOptions,
  WidgetDataType,
} from 'redux/modules/superreports.types';
import { getWidgetMetadata } from 'redux/selectors/superreports';
import type { Features } from 'types/account';
import type { ThunkDispatch } from 'types/redux';
import { notNull } from 'utils/typesafe';
import styles from './DonutWidget.module.scss';

const DATA_TYPE_OPTIONS: ReadonlyArray<DropdownOption> = [
  {
    name: 'Hours',
    action: 'hours',
  },
  {
    name: 'Money',
    action: 'money',
  },
];

export interface DonutWidgetHeaderProps {
  features: Features;
  readOnly?: boolean;
  widget: WidgetDraft;
}

const DonutWidgetHeader: React.FC<DonutWidgetHeaderProps> = ({ widget, readOnly }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const widgetMetadata = useSelector(getWidgetMetadata);
  const groupByOptions = useMemo(
    () =>
      [
        {
          name: widgetMetadata[WIDGET_BILLABLE_VS_NON_BILLABLE_DONUT]?.title,
          action: WIDGET_BILLABLE_VS_NON_BILLABLE_DONUT,
        },
        {
          name: widgetMetadata[WIDGET_BILLED_VS_UNBILLED_DONUT]?.title,
          action: WIDGET_BILLED_VS_UNBILLED_DONUT,
        },
        {
          name: widgetMetadata[WIDGET_CLIENTS_DONUT]?.title,
          action: WIDGET_CLIENTS_DONUT,
        },
        {
          name: widgetMetadata[WIDGET_PROJECTS_DONUT]?.title,
          action: WIDGET_PROJECTS_DONUT,
        },
        {
          name: widgetMetadata[WIDGET_TEAMS_DONUT]?.title,
          action: WIDGET_TEAMS_DONUT,
        },
        {
          name: widgetMetadata[WIDGET_LABELS_DONUT]?.title,
          action: WIDGET_LABELS_DONUT,
        },
        {
          name: widgetMetadata[WIDGET_LINKED_WORK_DONUT]?.title,
          action: WIDGET_LINKED_WORK_DONUT,
        },
      ].filter(notNull) as Array<DropdownOption>,
    [widgetMetadata]
  );

  const handleChangeGroupBy = (option: DropdownOption) => {
    if (option.action === widget.widget_type) return;

    dispatch(
      changeWidgetType({
        widgetReference: widget.id || widget._ref,
        widgetType: option.action!,
      })
    );

    if (NON_MONEY_WIDGETS.includes(option.action!)) {
      dispatch(
        updateWidgetOptions({
          reference: widget.id || widget._ref,
          options: {
            dataType: WidgetDataType.Hours,
          },
        })
      );
    }
    dispatch(fetchReportData(false));
  };

  const handleChangeDataType = (option: DropdownOption) => {
    if (option.action === widget.options!.dataType) return;

    dispatch(
      updateWidgetOptions({
        reference: widget.id || widget._ref,
        options: {
          dataType: option.action as any,
        },
      })
    );
  };

  const dataType =
    DATA_TYPE_OPTIONS.find(option => option.action === widget.options!.dataType)?.name ?? '???';

  return (
    <>
      {!readOnly ? (
        <PopoverDropdownList
          className={styles.titleWrapper}
          options={groupByOptions}
          onClick={handleChangeGroupBy}
          target={
            <Button priority="low" className={styles.title}>
              {widget.title}
            </Button>
          }
        />
      ) : (
        widget.title
      )}
      <span className={styles.by}>
        {!NON_MONEY_WIDGETS.includes(widget.widget_type) && 'by\u00A0'}
      </span>

      {!readOnly && widget.widget_type !== WIDGET_BILLABLE_VS_NON_BILLABLE_DONUT ? (
        <PopoverDropdownList
          className={styles.titleWrapper}
          options={DATA_TYPE_OPTIONS}
          onClick={handleChangeDataType}
          target={
            <Button priority="low" className={styles.title}>
              {dataType}
            </Button>
          }
        />
      ) : widget.widget_type === WIDGET_BILLABLE_VS_NON_BILLABLE_DONUT ? (
        DATA_TYPE_OPTIONS[0].name // Index of whatever value holds 'Hours'
      ) : (
        dataType
      )}
    </>
  );
};

export default DonutWidgetHeader;
